import React, {useState, createElement} from "react";
import {useIntl} from "react-intl";
import {
  TextArea,
  TextInput,
  NumberInput,
  DropDown,
} from "@spg/component_library";

type Without<Type1, Type2> = {[P in Exclude<keyof Type1, keyof Type2>]?: never};

export type XOR<Type1, Type2> = Type1 | Type2 extends object
  ? (Without<Type1, Type2> & Type2) | (Without<Type2, Type1> & Type1)
  : Type1 | Type2;

/**
 * A wrapper component that implements standard behavior for inputs in the SCH:
 * An error message is shown on required inputs when they lose focus while empty.
 */
function DefaultInputBehavior<
  T extends
    | typeof DropDown
    | typeof TextArea
    | typeof TextInput
    | typeof NumberInput,
>(
  props: React.ComponentPropsWithoutRef<T> & {
    /** The component to wrap */
    component: T;
    /** The explanation of the inputs purpose or an error message displayed underneath it */
    supportingText?: React.ComponentPropsWithoutRef<T>["supportingText"];
    /** A function to call when the input loses focus */
    onBlur?: React.ComponentPropsWithoutRef<T>["onBlur"];
    /** Whether to give an indication when the input is blurred while empty */
    isRequired?: React.ComponentPropsWithoutRef<T>["isRequired"];
    /** The currently selected option – can be implemented as a useState()-pair with setSelectedOption (only used for DropDowns) */
    selectedOption?: T extends typeof DropDown
      ? React.ComponentPropsWithoutRef<typeof DropDown>["selectedOption"]
      : never;
    /** The currently entered text – can be implemented as a useState()-pair with setValue (not used for DropDowns) */
    value?: T extends typeof DropDown
      ? never
      : React.ComponentPropsWithoutRef<Exclude<T, typeof DropDown>>["value"];
  },
) {
  const intl = useIntl();

  const [hadFocus, setHadFocus] = useState(false);

  const isEmpty =
    "selectedOption" in props
      ? props.selectedOption === null
      : props.value.trim().length === 0;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // T is not exclusively DropDown/TextArea/… but a union of all, so the component asks for all props used for any of the inputs
  // as long as only a single input is passed for the component-prop, there shouldn't be any problems
  return createElement(props.component, {
    ...props,
    onBlur: () => {
      if (props.onBlur) props.onBlur();
      setHadFocus(true);
    },
    supportingText:
      props.supportingText ||
      (props.isRequired && hadFocus && isEmpty
        ? intl.formatMessage({
            id: "reusables.defaultInputBehavior.error.required",
          })
        : undefined),
  });
}

export {DefaultInputBehavior};
